import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export const resolveDeadlineVariant = deadline => {
  if (deadline === 4) return 'light-success'
  if (deadline === 3) return 'light-info'
  if (deadline === 2) return 'light-warning'
  return 'light-danger'
}

export const resolveDeleteStatusVariant = status => {
  if (status === 'synchronized') return 'info'
  return 'warning'
}

export const resolveStatusVariant = deadline => {
  if (deadline === 'synchronized') return 'info'
  if (deadline === 'pending') return 'warning'
  return 'success'
}

export default function useUserList() {
  // Use toast
  const toast = useToast()

  const refListTable = ref(null)

  // Table Handlers
  const tableColumns = [
    { key: 'created_at', label: 'CRIADO', sortable: true },
    {
      key: 'code',
      sortable: true,
      label: 'REMESSA',
      sortKey: 'code',
    },
    {
      key: 'payment',
      label: 'PAGAMENTOS',
      sortable: false,
    },
    {
      key: 'amount_without_discount',
      label: 'VALOR',
      sortable: true,
    },
    {
      key: 'amount_with_discount',
      label: 'À PAGAR',
      sortable: true,
    },
    {
      key: 'status',
      label: 'STATUS',
      sortable: false,
    },
    {
      key: 'deadline',
      label: 'PRAZO',
      sortable: false,
    },
    {
      key: 'actions',
      label: 'Ações',
      sortable: false,
    },
  ]
  const perPage = ref(25)
  const totalRemittances = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const showImediato = ref(false)
  const searchQuery = ref('')
  const sortBy = ref('name')
  const isSortDirDesc = ref(true)
  const situationFilter = ref(null)
  const statusFilter = ref(null)
  const contractList = ref([])

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalRemittances.value,
    }
  })

  const refetchData = () => {
    refListTable.value.refresh()
  }

  watch([currentPage, perPage, searchQuery, situationFilter, statusFilter, showImediato], () => {
    refetchData()
  })

  const fetchPaymentRemittances = (ctx, callback) => {
    store
      .dispatch('app-contract/fetchPaymentRemittances', {
        q: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDirection: isSortDirDesc.value ? 'desc' : 'asc',
        situation: situationFilter.value,
        status: statusFilter.value,
        show_imediato: showImediato.value ? 1 : 0,
      })
      .then(response => {
        const { data } = response.data
        contractList.value = data
        callback(contractList.value)
        totalRemittances.value = response.data.meta.pagination.total
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao buscar ',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const createPaymentRemittance = async () => {
    const items = contractList.value.filter(contract => contract.checked).map(contract => ({ contract_id: contract.id, operation: null }))
    try {
      const { data } = await store
        .dispatch('app-contract/createPaymentRemittance', {
          items,
        })
      return {
        id: data.data.id,
        code: data.data.code,
      }
    } catch (err) {
      toast({
        component: ToastificationContent,
        props: {
          title: 'Erro',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
    }
    return null
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveUserRoleVariant = role => {
    if (role === 'subscriber') return 'primary'
    if (role === 'author') return 'warning'
    if (role === 'maintainer') return 'success'
    if (role === 'editor') return 'info'
    if (role === 'admin') return 'danger'
    return 'primary'
  }

  const resolveUserRoleIcon = role => {
    if (role === 'subscriber') return 'UserIcon'
    if (role === 'author') return 'SettingsIcon'
    if (role === 'maintainer') return 'DatabaseIcon'
    if (role === 'editor') return 'Edit2Icon'
    if (role === 'admin') return 'ServerIcon'
    return 'UserIcon'
  }

  return {
    contractNotInPaymentRemittance: fetchPaymentRemittances,
    tableColumns,
    perPage,
    currentPage,
    totalUsers: totalRemittances,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refUserListTable: refListTable,

    resolveUserRoleVariant,
    resolveUserRoleIcon,
    resolveDeadlineVariant,
    resolveStatusVariant,
    refetchData,
    showImediato,

    // Extra Filters
    situationFilter,
    statusFilter,
    createPaymentRemittance,
  }
}
